import React from "react";
import { Card } from "antd";
import { Globe2, Lightbulb } from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../../../context/LanguageContext";
import content from "../../../utils/content";

export function MissionSection() {
  const { language } = useLanguage();
  const t = content[language].mission;

  // Intersection Observer für Header, Image und Cards
  const [refHeader, inViewHeader] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [refImage, inViewImage] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [refCards, inViewCards] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // Animation Controls
  const headerControls = useAnimation();
  const imageControls = useAnimation();
  const cardsControls = useAnimation();

  React.useEffect(() => {
    if (inViewHeader) headerControls.start("visible");
    if (inViewImage) imageControls.start("visible");
    if (inViewCards) cardsControls.start("visible");
  }, [
    inViewHeader,
    inViewImage,
    inViewCards,
    headerControls,
    imageControls,
    cardsControls,
  ]);

  // Framer Motion Variants
  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeOut", delay },
    },
  });

  const fadeInLeft = (delay = 0) => ({
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, ease: "easeOut", delay },
    },
  });

  const fadeInRight = (delay = 0) => ({
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 1, ease: "easeOut", delay },
    },
  });

  return (
    <div
      className="w-full px-8 md:px-12 py-12 md:py-2 overflow-hidden"
      id="About"
    >
      {/* Header Section */}
      <motion.div
        ref={refHeader}
        variants={fadeInUp(0)}
        initial="hidden"
        animate={headerControls}
        className="text-left mb-12"
      >
        <h2 className="lg:text-[5rem] text-4xl font-bold tracking-tight bg-white py-16 leading-[4rem]">
          <span className="text-[#FF7051]">{t.header.title.highlight}</span>
          {t.header.title.rest}
        </h2>
      </motion.div>

      {/* Main Content */}
      <div className="lg:grid gap-12 flex flex-col lg:grid-cols-[1fr_15%]">
        {/* Image Section */}
        <motion.div
          ref={refImage}
          variants={fadeInLeft(0.3)}
          initial="hidden"
          animate={imageControls}
          className="flex items-center justify-center order-1 lg:order-1 lg:mr-20"
        >
          <img
            src="./9.png"
            alt="Three yellow stars illustration"
            width="400"
            height="300"
            className="w-full lg:max-w-[800px]"
          />
        </motion.div>

        {/* Card Section */}
        <motion.div
          ref={refCards}
          variants={fadeInRight(0.5)}
          initial="hidden"
          animate={cardsControls}
          className="space-y-6 order-2 lg:order-2 relative"
        >
          <div className="lg:absolute lg:top-[50%] lg:-translate-y-[50%] lg:right-[70%] flex flex-col gap-16">
            {/* Mission Card */}
            <motion.div
              variants={fadeInUp(0.6)}
              initial="hidden"
              animate={cardsControls}
            >
              <Card>
                <Card.Meta
                  title={
                    <div className="flex flex-col items-center gap-4 text-2xl mb-4 whitespace-pre-wrap md:whitespace-nowrap">
                      <Globe2 className="lg:h-5 lg:w-5 h-12 w-12 text-[#FF7051]" />
                      {t.missionCard.title}
                    </div>
                  }
                  description={
                    <p className="text-gray-500 text-sm">
                      {t.missionCard.description}
                    </p>
                  }
                />
              </Card>
            </motion.div>

            {/* Vision Card */}
            <motion.div
              variants={fadeInUp(0.8)}
              initial="hidden"
              animate={cardsControls}
            >
              <Card className="bg-[#FF7051] text-white">
                <Card.Meta
                  title={
                    <div className="flex flex-col items-center gap-4 text-2xl mb-4 whitespace-pre-wrap md:whitespace-nowrap">
                      <Lightbulb className="lg:h-5 lg:w-5 h-12 w-12 text-white" />
                      {t.visionCard.title}
                    </div>
                  }
                  description={
                    <p className="text-white/90">{t.visionCard.description}</p>
                  }
                />
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default MissionSection;
